import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { NAV, type ViewId } from './nav-items';
import logoExpandido from '../../../public/assets/brand/logo-expandido.svg?url';
import logoColapsado from '../../../public/assets/brand/logo-colapsado.svg?url';
import './nav.css';

export interface SidebarProps {
  current: ViewId;
  onNavigate: (id: ViewId) => void;
  isAdmin: boolean;
  /** Recolhida para trilha de ícones (só ≥1024px). */
  collapsed: boolean;
  /** Gaveta aberta (só <1024px). */
  open: boolean;
  onClose: () => void;
  /** Id do elemento, para o botão da barra superior apontar com aria-controls. */
  id: string;
}

/**
 * Um único componente para os dois comportamentos.
 *
 * A versão anterior tinha markup separado para desktop e mobile, com estados
 * independentes: dava para deixar a barra recolhida e a gaveta aberta ao mesmo
 * tempo, e os dois botões de menu tinham `aria-expanded` que se contradiziam.
 * Aqui há um estado e um botão; o meio (largura) decide o que ele significa.
 */
export function Sidebar({ current, onNavigate, isAdmin, collapsed, open, onClose, id }: SidebarProps) {
  const navId = id;
  const firstItem = useRef<HTMLButtonElement>(null);

  // Gaveta aberta: Esc fecha, e o foco entra nela. Sem isso o teclado continua
  // navegando o conteúdo atrás de um painel que cobre a tela.
  useEffect(() => {
    if (!open) return;
    firstItem.current?.focus();
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  let index = 0;

  return (
    <>
      <aside className="sidebar" data-collapsed={collapsed} data-open={open} id={navId}>
        {/* O controle de menu NÃO mora aqui. Ele vive na barra superior, fora
            da gaveta: com o botão dentro dela, uma gaveta fechada em 390px
            deixava o produto sem nenhuma forma de abrir a navegação. */}
        <div className="sidebar__brand" aria-label="Setter WA">
          <img
            className="sidebar__logo sidebar__logo--expandido"
            src={logoExpandido}
            alt=""
            width="162"
            height="34"
          />
          <img
            className="sidebar__logo sidebar__logo--colapsado"
            src={logoColapsado}
            alt=""
            width="23"
            height="34"
          />
        </div>

        <nav aria-label="Principal">
          {NAV.map((group) => {
            const labelId = `${navId}-${group.label}`;
            return (
              <div className="nav__group" key={group.label} role="group" aria-labelledby={labelId}>
                <p className="nav__group-label" id={labelId}>{group.label}</p>
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const blocked = item.adminOnly && !isAdmin;
                  const ref = index++ === 0 ? firstItem : undefined;
                  return (
                    <button
                      key={item.id}
                      ref={ref}
                      type="button"
                      className="nav__item"
                      aria-current={current === item.id ? 'page' : undefined}
                      disabled={blocked || undefined}
                      /* Desabilitado ainda precisa dizer por quê. Um item que
                         apenas some faz procurar por uma função que existe. */
                      title={blocked ? 'Disponível apenas para administradores' : undefined}
                      onClick={() => { onNavigate(item.id); onClose(); }}
                    >
                      <Icon className="nav__icon" size={20} aria-hidden="true" />
                      <span className="nav__label">{item.label}</span>
                      {/* A dica só existe quando a barra está recolhida.
                          Antes ela ficava sempre no DOM com `opacity: 0`, e o
                          texto do item aparecia duplicado para qualquer coisa
                          que leia `innerText` — inclusive testes e
                          ferramentas de automação. `aria-hidden` resolve para
                          leitor de tela, não para o resto. */}
                      {collapsed && <span className="nav__tip" aria-hidden="true">{item.label}</span>}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </nav>
      </aside>

      {/* Fundo clicável só existe quando a gaveta está aberta. É <button> e não
          <div onClick>: assim entra na ordem de tabulação e tem nome. */}
      {open && (
        <button type="button" className="scrim" onClick={onClose} aria-label="Fechar menu">
          <X className="sr-only" aria-hidden="true" />
        </button>
      )}
    </>
  );
}
